import {
  type PDFPage,
  type PDFFont,
  rgb,
  pushGraphicsState,
  popGraphicsState,
  moveTo,
  lineTo,
  closePath,
  clip,
  endPath,
} from "pdf-lib";
import type { Mark, Point } from "../shared/schema";
import {
  markPresentation,
  measurementLabel,
  validColor,
} from "../shared/mark-presentation";
export function pdfColor(value: unknown) {
  const c = validColor(value);
  return rgb(
    parseInt(c.slice(1, 3), 16) / 255,
    parseInt(c.slice(3, 5), 16) / 255,
    parseInt(c.slice(5, 7), 16) / 255,
  );
}
/** Vector annotations use the same label and decoration geometry as the canvas. */
export function exportMark(
  page: PDFPage,
  font: PDFFont,
  shape: Mark,
  meters: number,
  factor: number,
  point: (p: Point) => Point,
) {
  const p = shape.props.points;
  if (p.length < 2) return;
  const style = markPresentation(shape),
    ink = pdfColor(shape.props.color),
    opacity = shape.opacity;
  const line = (
    a: Point,
    b: Point,
    dash = false,
    color = ink,
    alpha = opacity,
  ) =>
    page.drawLine({
      start: point(a),
      end: point(b),
      thickness: Math.max(0.1, shape.props.weight * factor),
      color,
      opacity: alpha,
      dashArray: dash ? style.dash.map((n) => n * factor) : undefined,
    });
  if (shape.props.kind === "area") {
    const q = p.map(point),
      path =
        q.map((v, i) => `${i ? "L" : "M"} ${v.x} ${-v.y}`).join(" ") + " Z";
    if (shape.meta.hatch === "solid")
      page.drawSvgPath(path, {
        color: pdfColor(style.fillColor),
        opacity: 0.16 * opacity,
      });
    else if (shape.meta.hatch !== "none") {
      page.pushOperators(
        pushGraphicsState(),
        moveTo(q[0].x, q[0].y),
        ...q.slice(1).map((v) => lineTo(v.x, v.y)),
        closePath(),
        clip(),
        endPath(),
      );
      // Rotate into hatch coordinates, generate bounded lines, then return to shape space.
      const angles =
        shape.meta.hatch === "cross"
          ? [style.angle, style.angle + 90]
          : [style.angle];
      for (const degrees of angles) {
        const angle = (degrees * Math.PI) / 180,
          c = Math.cos(angle),
          s = Math.sin(angle),
          rot = p.map((v) => ({ x: v.x * c + v.y * s, y: -v.x * s + v.y * c }));
        const minX = Math.min(...rot.map((v) => v.x)),
          maxX = Math.max(...rot.map((v) => v.x)),
          minY = Math.min(...rot.map((v) => v.y)),
          maxY = Math.max(...rot.map((v) => v.y));
        const spacing = Math.max(style.spacing, (maxX - minX) / 3000),
          unrot = (x: number, y: number) => ({
            x: x * c - y * s,
            y: x * s + y * c,
          });
        for (
          let x = Math.floor(minX / spacing) * spacing;
          x <= maxX;
          x += spacing
        )
          page.drawLine({
            start: point(unrot(x, minY)),
            end: point(unrot(x, maxY)),
            thickness: factor,
            color: pdfColor(style.fillColor),
            opacity: 0.35 * opacity,
          });
      }
      page.pushOperators(popGraphicsState());
    }
    line(p[p.length - 1], p[0], true);
  }
  for (let i = 1; i < p.length; i++) line(p[i - 1], p[i], true);
  for (const decoration of style.decorations)
    for (let i = 1; i < decoration.length; i++)
      line(decoration[i - 1], decoration[i]);
  const label = measurementLabel(shape, meters);
  if (shape.props.kind !== "line" && label) {
    const at = point(style.label),
      size = 15 * factor,
      width = font.widthOfTextAtSize(label, size),
      height = 25 * factor;
    page.drawRectangle({
      x: at.x - Math.max(52 * factor, width / 2 + 6 * factor),
      y: at.y - 11 * factor,
      width: Math.max(104 * factor, width + 12 * factor),
      height,
      color: rgb(1, 1, 1),
      opacity: 0.96 * opacity,
    });
    page.drawText(label, {
      x: at.x - width / 2,
      y: at.y - 4 * factor,
      size,
      font,
      color: ink,
      opacity,
    });
  }
}
